import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Crown, Check, Storefront, Gauge, PaintBrush } from "@phosphor-icons/react";

const BG = "https://images.unsplash.com/photo-1559619081-8fd2cc754ca3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function BuyPlatform() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [brand, setBrand] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [purchaseOn, setPurchaseOn] = useState(false);
  useEffect(() => { api.get("/store-settings").then(({ data }) => setPurchaseOn(!!data.platform)).catch(() => {}); }, []);

  useEffect(() => { api.get("/platform-license").then((r) => setSettings(r.data)); }, []);

  const buy = async () => {
    setErr("");
    if (!user) { navigate("/login"); return; }
    if (user.owns_platform) { setErr("You already own a platform. Open your dashboard."); return; }
    if (!brand.trim()) { setErr("Please enter your brand name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout-license", { origin_url: window.location.origin, brand_name: brand });
      window.location.href = data.url;
    } catch (e) { setErr(e.response?.data?.detail || "Could not start checkout."); setBusy(false); }
  };

  const perks = [
    { icon: <Storefront size={22} weight="bold" color="#FF007F" />, t: "Your own branded storefront", d: "A public deals site at your own web address with YOUR name on it." },
    { icon: <Gauge size={22} weight="bold" color="#00FFFF" />, t: "Your own admin dashboard", d: "Add categories, links and ads, and see your stats — fully separate from everyone else." },
    { icon: <PaintBrush size={22} weight="bold" color="#FFD600" />, t: "Set your own prices", d: "Charge members to post links & ads on YOUR store and keep the revenue." },
  ];

  return (
    <div>
      <section className="relative overflow-hidden">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/75" />
        <div className="relative max-w-4xl mx-auto px-6 py-20 text-center">
          <Crown size={44} weight="fill" color="#FFD600" className="mx-auto" />
          <h1 className="font-display text-4xl sm:text-6xl font-black uppercase tracking-tighter mt-4">
            {settings?.headline || "Own the Whole Platform"}
          </h1>
          <p className="text-zinc-300 max-w-2xl mx-auto mt-4">{settings?.description}</p>
          {settings && (
            <p className="font-mono font-extrabold text-5xl text-[#39FF14] mt-6">${settings.price}<span className="text-lg text-zinc-400"> one-time</span></p>
          )}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-14 grid md:grid-cols-3 gap-5">
        {perks.map((p) => (
          <div key={p.t} className="border border-zinc-800 bg-[#18181B] p-6">
            {p.icon}
            <h3 className="font-display font-bold text-lg mt-3">{p.t}</h3>
            <p className="text-sm text-zinc-400 mt-1">{p.d}</p>
          </div>
        ))}
      </section>

      <section className="max-w-xl mx-auto px-6 pb-20">
        <div className="border-2 border-[#FFD600] bg-[#18181B] p-8 hard-shadow-cyan">
          <h2 className="font-display text-2xl font-black uppercase mb-4">Claim Your Platform</h2>
          {err && <p className="text-[#FF007F] text-sm mb-4 border border-[#FF007F] p-3" data-testid="buy-error">{err}</p>}
          <label className="text-xs uppercase tracking-widest text-zinc-400">Your brand name *</label>
          <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="e.g. Mega Deals Hub" data-testid="buy-brand-input"
            className="w-full mt-1 mb-2 bg-zinc-900 border border-zinc-700 focus:border-[#FFD600] outline-none px-3 py-3 text-white" />
          <p className="text-xs text-zinc-500 mb-6">Your storefront address will be generated from this name (you can rename anytime in your dashboard).</p>
          {purchaseOn ? (
          <button onClick={buy} disabled={busy} data-testid="buy-platform-button"
            className="w-full py-4 bg-[#FFD600] text-black font-black uppercase tracking-widest border border-[#FFD600] hover:bg-transparent hover:text-[#FFD600] transition-colors disabled:opacity-50">
            {busy ? "Redirecting…" : `Buy My Platform — $${settings?.price ?? ""}`}
          </button>
          ) : (
          <button disabled data-testid="buy-platform-button"
            className="w-full py-4 bg-zinc-700 text-zinc-300 font-black uppercase tracking-widest border border-zinc-600 cursor-not-allowed">
            Coming Soon
          </button>
          )}
          <p className="text-xs text-zinc-500 mt-3">{purchaseOn ? "Secure checkout via Stripe. Cash App Pay & cards accepted." : "🚀 Platform purchasing opens soon! We're finalizing checkout — check back shortly to launch your own site."}</p>
        </div>
      </section>
    </div>
  );
}
